import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  setSignupData,
  setError,
  setVerificationStep,
} from "../../store/slices/signupSlice";
import {
  setCredentials,
  updateUserTypeSelected,
} from "../../store/slices/authSlice";
import { searchService, authService } from "../../services/api";
import { useDebounce } from "use-debounce";
import { club_setup_icon, pencil_edit } from "../../constants/icons";
import { useDebounce as useDebounceHook } from "../../hooks/useDebounce";



const STATIC_POSITIONS = [
  { id: 1, name: "Sporting Director" },
  { id: 2, name: "Director of Football" },
  { id: 3, name: "Technical Director" },
];

const ClubDetailsStep = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tokens } = useSelector((state) => state.auth);
  const { error } = useSelector((state) => state.signup);
  const auth = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [positionQuery, setPositionQuery] = useState("");
  const [showPositionDropdown, setShowPositionDropdown] = useState(false);
  const [positionResults, setPositionResults] = useState(STATIC_POSITIONS);
  const positionDropdownRef = useRef(null);

  const [debouncedPositionValue] = useDebounce(positionQuery, 300);

  // Club search modal states
  const [clubSearchQuery, setClubSearchQuery] = useState('');
  const [clubSearchResults, setClubSearchResults] = useState([]);
  const [searchPagination, setSearchPagination] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const searchInputRef = useRef(null);

  // Debounced search
  const debouncedSearchQuery = useDebounceHook(clubSearchQuery, 300);

  const [formData, setFormData] = useState({
    selectedClub: null,
    clubLicenseNumber: "",
    firstName: "",
    lastName: "",
    contactPersonPosition: "",
  });

  // Search clubs when query changes or page changes
  useEffect(() => {
    if (debouncedSearchQuery.trim() && showSearchModal) {
      searchClubs(debouncedSearchQuery, 1);
      setCurrentPage(1);
    } else if (!debouncedSearchQuery.trim()) {
      setClubSearchResults([]);
      setSearchPagination(null);
      setIsSearching(false);
    }
  }, [debouncedSearchQuery, showSearchModal]);

  const searchClubs = async (query, page = 1) => {
    setIsSearching(true);
    try {
      const params = {
        type: 'clubs',
        query: query,
        per_page: 10,
        page: page
      };

      const response = await searchService.search(params);
      
      if (response.success && response.data) {
        // Updated to handle new response structure
        const clubs = response.data.results?.clubs || [];
        setClubSearchResults(clubs);
        setSearchPagination(response.pagination || null);
      } else {
        setClubSearchResults([]);
        setSearchPagination(null);
      }
    } catch (error) {
      console.error('Club search error:', error);
      setClubSearchResults([]);
      setSearchPagination(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePageChange = (page) => {
    if (clubSearchQuery.trim()) {
      setCurrentPage(page);
      searchClubs(clubSearchQuery, page);
    }
  };

  const handleClubSelect = (club) => {
    setFormData((prev) => ({
      ...prev,
      selectedClub: club,
    }));
    setShowSearchModal(false);
    setClubSearchQuery('');
    setClubSearchResults([]);
    setSearchPagination(null);
  };

  const handleEditClub = () => {
    setFormData(prev => ({ ...prev, selectedClub: null }));
    setShowSearchModal(true);
    setClubSearchQuery('');
    setClubSearchResults([]);
    setSearchPagination(null);
  };

  const handleOpenSearch = () => {
    setShowSearchModal(true);
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  const handleBack = () => {
    dispatch(setVerificationStep("type"));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.selectedClub) {
      dispatch(setError("Please select a club"));
      return;
    }

    if (
      !formData.clubLicenseNumber ||
      !formData.firstName ||
      !formData.lastName ||
      !formData.contactPersonPosition
    ) {
      dispatch(setError("Please fill in all required fields"));
      return;
    }

    setLoading(true);
    dispatch(setError(null));

    const clubData = {
      wyscout_id: Number(formData.selectedClub.wyscout_id),
      first_name: String(formData.firstName).trim(),
      last_name: String(formData.lastName).trim(),
      contact_person_position: String(formData.contactPersonPosition).trim(),
      licence: String(formData.clubLicenseNumber).trim(),
    };

    try {
      const response = await authService.createClub(clubData);

      if (response.success) {
        dispatch(setSignupData({ clubDetails: clubData }));
        dispatch(setVerificationStep("complete"));
      } else {
        dispatch(setError(response.error || "Failed to create club"));
      }
    } catch (error) {
      console.error("Submit error:", error);
      dispatch(setError("An unexpected error occurred"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handlePositionSearch(debouncedPositionValue);
  }, [debouncedPositionValue]);

  const handlePositionSearch = (value) => {
    if (!value || value.length < 1) {
      setPositionResults(STATIC_POSITIONS);
      return;
    }

    const filteredPositions = STATIC_POSITIONS.filter((position) =>
      position.name.toLowerCase().includes(value.toLowerCase())
    );

    setPositionResults(filteredPositions);
  };

  const handlePositionInputFocus = () => {
    setShowPositionDropdown(true);
  };

  const handlePositionSelect = (position) => {
    setFormData((prev) => ({
      ...prev,
      contactPersonPosition: position.name,
    }));
    setPositionQuery(position.name);
    setShowPositionDropdown(false);
  };

  const handlePositionInputChange = (e) => {
    const value = e.target.value;
    setPositionQuery(value);
    setFormData((prev) => ({
      ...prev,
      contactPersonPosition: value,
    }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        positionDropdownRef.current &&
        !positionDropdownRef.current.contains(event.target)
      ) {
        setShowPositionDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="py-4">
      <div className="flex items-center mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5 mr-2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5L8.25 12l7.5-7.5"
            />
          </svg>
          Back
        </button>
      </div>

      <div className="w-full">
        <div className="flex items-center justify-center w-12 h-12 bg-[#2283FF] rounded-2xl mb-6">
          <img
            src={club_setup_icon}
            alt="Club Setup Icon"
            className="w-full h-full object-contain"
          />
        </div>

        <h2 className="text-2xl leading-[28px] font-bold text-gray-900 mb-2">
          Setup Club
        </h2>
        <p className="text-[14px] leading-[20px] text-gray-600 mb-8">
          Find your club and provide the admin details for your account
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Club Selection */}
          <div className="space-y-3">
            <label className="block text-[16px] font-medium text-black mb-1">
              Find Club
            </label>
            
            <div className="border border-gray-300 rounded-lg h-12 flex items-center">
              {formData.selectedClub ? (
                <div className="flex items-center justify-between w-full px-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded-full bg-gray-300 bg-cover bg-center"
                      style={{
                        backgroundImage: formData.selectedClub.image_path ? `url(${formData.selectedClub.image_path})` : 'none'
                      }}
                    />
                    <span className="text-[16px] text-black font-medium">{formData.selectedClub.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleEditClub}
                    className="text-secondary hover:text-gray-900"
                  >
                    <img
                      src={pencil_edit}
                      alt="Edit Icon"
                      className="w-5 h-5 cursor-pointer"
                    />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleOpenSearch}
                  className="w-full h-full px-4 text-left"
                >
                  <span className="text-[16px] text-black/60 font-medium">Search your club here</span>
                </button>
              )}
            </div>
          </div>

          {/* Club Search Modal */}
          {showSearchModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[80vh] flex flex-col">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b">
                  <h2 className="text-xl font-semibold text-[#202630]">Search Clubs</h2>
                  <button
                    type="button"
                    onClick={() => setShowSearchModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Search Input */}
                <div className="p-6 border-b">
                  <div className="relative">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search for a club..."
                      value={clubSearchQuery}
                      onChange={(e) => setClubSearchQuery(e.target.value)}
                      className="w-full h-12 pl-12 pr-4 border border-[#E9E9E9] rounded-[5px] text-base placeholder-[#626262] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <svg className="w-5 h-5 text-[#626262] absolute left-4 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    {isSearching && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2">
                        <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Search Results */}
                <div className="flex-1 overflow-y-auto p-6">
                  {isSearching && clubSearchResults.length === 0 ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="text-center">
                        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
                        <p className="text-secondary">Searching clubs...</p>
                      </div>
                    </div>
                  ) : clubSearchResults.length > 0 ? (
                    <div className="space-y-4">
                      {/* Results List */}
                      <div className="space-y-2">
                        {clubSearchResults.map((club) => (
                          <button
                            key={club.id}
                            type="button"
                            onClick={() => handleClubSelect(club)}
                            className="w-full p-4 border border-[#E9E9E9] rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors text-left"
                          >
                            <div className="flex items-center gap-4">
                              <div 
                                className="w-12 h-12 rounded-full bg-gray-300 bg-cover bg-center flex-shrink-0"
                                style={{
                                  backgroundImage: club.image_path && !club.image_path.includes('placeholder') 
                                    ? `url(${club.image_path})` 
                                    : 'none'
                                }}
                              />
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <h3 className="text-base font-medium text-[#1A212F] truncate">
                                    {club.name}
                                  </h3>
                                </div>
                                
                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                  {club.country?.name && (
                                    <span className="truncate">{club.country.name}</span>
                                  )}
                                  
                                  {club.division?.name && (
                                    <span className="flex-shrink-0">{club.division.name}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>

                      {/* Pagination */}
                      {searchPagination && (
                        <div className="pt-4 border-t">
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-secondary">
                              Showing {clubSearchResults.length} results
                              
                            </div>
                            
                            {(searchPagination.total_pages > 1 || searchPagination.has_more) && (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handlePageChange(currentPage - 1)}
                                  disabled={currentPage <= 1}
                                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                  Previous
                                </button>
                                
                                <span className="px-2 text-sm text-secondary">
                                  {currentPage}
                                </span>
                                
                                <button
                                  type="button"
                                  onClick={() => handlePageChange(currentPage + 1)}
                                  disabled={!searchPagination.has_more && currentPage >= searchPagination.total_pages}
                                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                  Next
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : clubSearchQuery.trim() && !isSearching ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="text-center">
                        <svg className="w-12 h-12 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <p className="text-secondary">No clubs found for "{clubSearchQuery}"</p>
                        <p className="text-sm text-gray-400 mt-1">Try adjusting your search terms</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-32">
                      <div className="text-center">
                        <svg className="w-12 h-12 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <p className="text-secondary">Start typing to search for clubs</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-[16px] text-black font-medium mb-1">
              Club License Number
            </label>
            <input
              type="text"
              className="text-black font-medium text-[15px] placeholder:text-black/60 placeholder:font-medium placeholder:text-[14px] placeholder:leading-[21px] w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
              placeholder="XX XX XX XX XX XX"
              value={formData.clubLicenseNumber}
              onChange={(e) =>
                setFormData({ ...formData, clubLicenseNumber: e.target.value })
              }
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[16px] text-black font-medium  block mb-1">
                First Name
              </label>
              <input
                type="text"
                className="text-black font-medium text-[15px] placeholder:text-black/60 placeholder:font-medium placeholder:text-[14px] placeholder:leading-[21px] w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
                placeholder="Ex: John"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                required
              />
            </div>
            
            <div>
              <label className="text-[16px] text-black font-medium  block mb-1">
                Last Name
              </label>
              <input
                type="text"
                className="text-black font-medium text-[15px] placeholder:text-black/60 placeholder:font-medium placeholder:text-[14px] placeholder:leading-[21px] w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
                placeholder="Ex: Doe"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                required
              />
            </div>
          </div>

          <div className="relative" ref={positionDropdownRef}>
            <label className="text-[16px] text-black font-medium block mb-1">
              Position
            </label>
            <div className="relative">
              <input
                type="text"
                className="text-black font-medium text-[15px] placeholder:text-black/60 placeholder:font-medium placeholder:text-[14px] placeholder:leading-[21px] w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
                placeholder="Select Position"
                value={positionQuery}
                onChange={handlePositionInputChange}
                onFocus={handlePositionInputFocus}
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 font-bold text-gray-700">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2.5}
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 9l6 6 6-6"
                  />
                </svg>
              </div>
            </div>

            {showPositionDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                {positionResults.map((position) => (
                  <div
                    key={position.id}
                    className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => handlePositionSelect(position)}
                  >
                    <div className="flex items-center justify-center w-5 h-5 mr-3">
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          formData.contactPersonPosition === position.name
                            ? "border-[#2283FF] bg-[#2283FF]"
                            : "border-gray-300"
                        }`}
                      >
                        {formData.contactPersonPosition === position.name && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                    </div>
                    <span className="text-sm text-gray-700">
                      {position.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !formData.selectedClub}
            className={`w-full py-2 px-4 rounded-lg transition-colors ${
              loading || !formData.selectedClub
                ? "bg-[#2283FF]/50 text-[#F5F5F5] cursor-not-allowed"
                : "bg-[#2283FF] hover:bg-[#1a6cd4] text-white"
            }`}
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ClubDetailsStep;
